import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Users, Calendar, FileText, Signature, Plus, Copy, ExternalLink, ChevronLeft, ChevronRight, Trash2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import PortalControls from "../components/PortalControls";

const Stat = ({ label, value, icon: Icon, to, testId }) => (
  <Link to={to || "#"} data-testid={testId}
    className="bg-white border border-stone-200 rounded-2xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 block">
    <div className="flex items-center justify-between mb-3">
      <Icon className="w-5 h-5 text-[#D4A373]" strokeWidth={1.25} />
    </div>
    <div className="font-heading text-3xl font-light">{value}</div>
    <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-1">{label}</p>
  </Link>
);

const CellPill = ({ ok, onClick, testId, title }) => {
  const cls = `w-full h-full flex items-center justify-center text-[11px] font-medium border-r border-stone-200 last:border-r-0 ${ok
    ? "bg-[#EEF5EB] text-[#4A6E42]"
    : "bg-[#FBEDEB] text-[#B4453E]"} ${onClick ? "cursor-pointer hover:brightness-95" : ""}`;
  const content = ok ? "✓" : "✗";
  if (onClick) return <button type="button" onClick={onClick} data-testid={testId} title={title} className={cls}>{content}</button>;
  return <span data-testid={testId} title={title} className={cls}>{content}</span>;
};

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function monthKey(d) { return `${d.getFullYear()}-${d.getMonth()}`; }

export default function Dashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [stats, setStats] = useState({ total_patients: 0, upcoming_appointments: 0, total_invoices: 0, total_consents: 0 });
  const [appointments, setAppointments] = useState([]);
  const [todayList, setTodayList] = useState([]);
  const [monthCursor, setMonthCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [patients, setPatients] = useState([]);
  const [consentTemplates, setConsentTemplates] = useState([]);
  const [showNewAppt, setShowNewAppt] = useState(false);
  const [savingAppt, setSavingAppt] = useState(false);
  const emptyForm = { patient_id: "", treatment: "", time: "10:00", duration_min: 60, type: "trattamento", consent_template_id: "", notes: "" };
  const [apptForm, setApptForm] = useState(emptyForm);

  const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const isTodaySelected = toISODate(selectedDate) === toISODate(new Date());
  const selectedIso = toISODate(selectedDate);

  useEffect(() => {
    const load = () => {
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/appointments").then((r) => setAppointments(r.data || [])).catch(() => {});
      api.get(`/dashboard/today?date=${selectedIso}`).then((r) => setTodayList(r.data || [])).catch(() => {});
    };
    load();
    const iv = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [selectedIso]);

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data || [])).catch(() => {});
    api.get("/consent-templates").then((r) => setConsentTemplates(r.data || [])).catch(() => {});
  }, []);

  function openNewAppt(prefillDate) {
    const d = prefillDate || selectedDate;
    setApptForm({ ...emptyForm });
    setSelectedDate(new Date(d));
    setShowNewAppt(true);
  }

  async function submitAppt(e) {
    e.preventDefault();
    if (!apptForm.patient_id || !apptForm.treatment) {
      toast.error(t("calendar.form.completeFields"));
      return;
    }
    setSavingAppt(true);
    try {
      const iso = new Date(`${selectedIso}T${apptForm.time}:00`).toISOString();
      await api.post("/appointments", {
        ...apptForm, date: iso, duration_min: Number(apptForm.duration_min),
      });
      toast.success(t("calendar.form.created"));
      setShowNewAppt(false);
      setApptForm(emptyForm);
      // Refresh
      const [a, dt] = await Promise.all([
        api.get("/appointments"),
        api.get(`/dashboard/today?date=${selectedIso}`),
      ]);
      setAppointments(a.data || []);
      setTodayList(dt.data || []);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingAppt(false); }
  }

  async function deleteAppt(id) {
    if (!window.confirm(t("calendar.confirmDelete"))) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success(t("common.deleted") || "Eliminato");
      const [a, dt] = await Promise.all([
        api.get("/appointments"),
        api.get(`/dashboard/today?date=${selectedIso}`),
      ]);
      setAppointments(a.data || []);
      setTodayList(dt.data || []);
    } catch (err) { toast.error(apiError(err)); }
  }

  async function toggleConfirmed(a) {
    const next = a.checks?.confirmed ? "scheduled" : "confirmed";
    try {
      await api.patch(`/appointments/${a.id}/status`, { status: next });
      setTodayList((list) => list.map((x) => x.id === a.id
        ? { ...x, status: next, checks: { ...x.checks, confirmed: next === "confirmed" } }
        : x));
      toast.success(next === "confirmed" ? t("dashboard.markedConfirmed") : t("dashboard.markedPending"));
    } catch (e) {
      toast.error(t("common.error"));
    }
  }

  const today = new Date();

  // Build the current month's calendar grid
  const grid = useMemo(() => {
    const y = monthCursor.getFullYear(), m = monthCursor.getMonth();
    const first = new Date(y, m, 1);
    const startWeekday = (first.getDay() + 6) % 7; // Monday = 0
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    // Group appointments by date string
    const map = {};
    for (const a of appointments) {
      const dt = new Date(a.date);
      if (dt.getFullYear() === y && dt.getMonth() === m) {
        const k = dt.getDate();
        map[k] = (map[k] || 0) + 1;
      }
    }
    return { cells, map };
  }, [monthCursor, appointments]);

  const monthLabel = monthCursor.toLocaleDateString(locale, { month: "long", year: "numeric" });
  const weekdays = useMemo(() => {
    const base = new Date(2024, 0, 1); // Monday
    return [...Array(7)].map((_, i) => {
      const d = new Date(base); d.setDate(base.getDate() + i);
      return d.toLocaleDateString(locale, { weekday: "short" });
    });
  }, [locale]);

  const bookingUrl = user ? `${window.location.origin}/reservar/${user.id}` : "";
  const copyBooking = () => { navigator.clipboard.writeText(bookingUrl); toast.success(t("common.linkCopied")); };

  const todayLabel = today.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="dashboard-page" className="space-y-10 fade-in-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("dashboard.eyebrow")}</p>
          <h1 className="font-heading text-5xl lg:text-6xl font-light tracking-tight">{t("dashboard.title")}</h1>
          <p className="text-stone-500 mt-2 capitalize">{todayLabel}</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => openNewAppt()} data-testid="new-appointment-shortcut"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 transition-colors duration-300 font-medium">
            <Plus className="w-4 h-4" strokeWidth={1.75} /> {t("calendar.newAppointment")}
          </button>
          <Link to="/pacientes" data-testid="new-patient-shortcut"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors duration-300 font-medium">
            <Plus className="w-4 h-4" strokeWidth={1.75} /> {t("dashboard.newPatient")}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("dashboard.statPatients")} value={stats.total_patients} icon={Users} to="/pacientes" testId="stat-patients" />
        <Stat label={t("dashboard.statAppointments")} value={stats.upcoming_appointments} icon={Calendar} to="/agenda" testId="stat-appointments" />
        <Stat label={t("dashboard.statInvoices")} value={stats.total_invoices} icon={FileText} to="/pacientes" testId="stat-invoices" />
        <Stat label={t("dashboard.statConsents")} value={stats.total_consents} icon={Signature} to="/plantillas" testId="stat-consents" />
      </div>


      {/* BIG: Selected day's agenda (defaults to today) */}
      <div className="bg-white border border-stone-200 rounded-2xl p-8" data-testid="today-calendar">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">
              {isTodaySelected ? t("dashboard.todayEyebrow") : t("dashboard.selectedDayEyebrow")}
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl font-light capitalize" data-testid="selected-day-title">
              {selectedDate.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {!isTodaySelected && (
              <button onClick={() => { setSelectedDate(new Date()); setMonthCursor(new Date()); }}
                data-testid="reset-to-today"
                className="text-xs uppercase tracking-[0.2em] text-[#B07D4F] hover:text-stone-900 border border-[#D4A373] rounded-full px-3 py-1.5">
                ← {t("common.today")}
              </button>
            )}
            <Link to="/agenda" className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900">
              {t("dashboard.viewAll")} →
            </Link>
          </div>
        </div>
        {todayList.length === 0 ? (
          <div className="py-16 text-center">
            <Calendar className="w-10 h-10 text-stone-300 mx-auto mb-4" strokeWidth={1.25} />
            <p className="text-stone-500">{t("dashboard.noToday")}</p>
            <button onClick={() => openNewAppt()} data-testid="add-appt-empty"
              className="inline-flex items-center gap-2 mt-4 text-sm text-[#B07D4F] underline underline-offset-4 hover:text-stone-900">
              <Plus className="w-3.5 h-3.5" /> {t("dashboard.addAppt")}
            </button>
          </div>
        ) : (
          (() => {
            // Compute working range clamped 6-22
            const starts = todayList.map((a) => new Date(a.start).getHours());
            const ends = todayList.map((a) => (a.end ? new Date(a.end) : new Date(a.start)).getHours());
            const minH = Math.max(6, Math.min(8, ...starts));
            const maxH = Math.min(22, Math.max(20, ...ends) + 1);
            const hours = [];
            for (let h = minH; h <= maxH; h++) hours.push(h);
            const byHour = {};
            for (const a of todayList) {
              const h = new Date(a.start).getHours();
              (byHour[h] = byHour[h] || []).push(a);
            }
            const fmt = (d) => d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
            return (
              <div className="overflow-x-auto -mx-2" data-testid="today-timeline">
                <table className="w-full text-sm border-collapse min-w-[980px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.15em] text-stone-400 bg-[#FAF9F6]">
                      <th className="text-left font-medium py-2 px-3 w-14 border-b border-stone-200">{t("dashboard.colHour")}</th>
                      <th className="text-left font-medium py-2 px-3 w-24 border-b border-stone-200">{t("dashboard.colRange")}</th>
                      <th className="text-left font-medium py-2 px-2 w-16 border-b border-stone-200">{t("dashboard.colType")}</th>
                      <th className="text-left font-medium py-2 px-3 border-b border-stone-200">{t("dashboard.colPatient")}</th>
                      <th className="text-left font-medium py-2 px-3 border-b border-stone-200">{t("dashboard.colTreatment")}</th>
                      <th className="font-medium py-2 px-1 w-14 border-b border-stone-200" title={t("dashboard.checkConfirmed")}>{t("dashboard.colConfirmedShort")}</th>
                      <th className="font-medium py-2 px-1 w-16 border-b border-stone-200" title={t("dashboard.checkConsent")}>{t("dashboard.colConsentShort")}</th>
                      <th className="font-medium py-2 px-1 w-16 border-b border-stone-200" title={t("dashboard.checkAnamnesi")}>{t("dashboard.colAnamnesiShort")}</th>
                      <th className="font-medium py-2 px-1 w-16 border-b border-stone-200" title={t("dashboard.checkIntervention")}>{t("dashboard.colInterventionShort")}</th>
                      <th className="font-medium py-2 px-1 w-14 border-b border-stone-200" title={t("dashboard.checkPhotos")}>{t("dashboard.colPhotosShort")}</th>
                      <th className="font-medium py-2 px-1 w-14 border-b border-stone-200" title={t("dashboard.checkInvoice")}>{t("dashboard.colInvoiceShort")}</th>
                      <th className="w-10 border-b border-stone-200"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hours.map((h) => {
                      const list = byHour[h] || [];
                      const hourLabel = `${String(h).padStart(2, "0")}:00`;
                      if (list.length === 0) {
                        return (
                          <tr key={h} data-testid={`hour-row-${h}`} className="border-b border-stone-100 hover:bg-[#FAF9F6]/60 cursor-pointer group"
                            onClick={() => { setApptForm({ ...emptyForm, time: hourLabel }); setShowNewAppt(true); }}>
                            <td className="py-1.5 px-3 text-stone-400 tabular-nums">{hourLabel}</td>
                            <td colSpan={11} className="py-1.5 px-3 text-stone-300 italic text-xs">
                              <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[#B07D4F] not-italic">
                                + {t("calendar.newAppointment")}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      return list.map((a, idx) => {
                        const s = new Date(a.start); const e = a.end ? new Date(a.end) : null;
                        const c = a.checks || {};
                        const isControl = a.type === "controllo";
                        const NA = <span className="text-stone-300">—</span>;
                        return (
                          <tr key={a.id} data-testid={`today-appt-${a.id}`} className="border-b border-stone-100 hover:bg-[#FAF9F6]">
                            <td className="py-1.5 px-3 text-stone-500 tabular-nums align-middle">{idx === 0 ? hourLabel : ""}</td>
                            <td className="py-1.5 px-3 text-stone-900 tabular-nums whitespace-nowrap align-middle">
                              {fmt(s)}{e ? `–${fmt(e)}` : ""}
                            </td>
                            <td className="py-1.5 px-2 align-middle">
                              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${isControl ? "bg-[#E7E5E4] text-stone-600" : "bg-[#D4A373]/20 text-[#B07D4F]"}`}>
                                {isControl ? t("dashboard.typeControl") : t("dashboard.typeTreatment")}
                              </span>
                            </td>
                            <td className="py-1.5 px-3 align-middle">
                              <Link to={`/pacientes/${a.patient_id}`} className="text-stone-900 hover:underline decoration-[#D4A373] underline-offset-4 whitespace-nowrap">
                                {a.patient_name || "—"}
                              </Link>
                            </td>
                            <td className="py-1.5 px-3 text-stone-600 whitespace-nowrap align-middle">
                              {a.treatment || <span className="text-stone-400 italic">{t("dashboard.noTreatment")}</span>}
                            </td>
                            <td className="p-0 align-middle border-l border-stone-200">
                              <div className="h-8"><CellPill ok={c.confirmed} onClick={() => toggleConfirmed(a)} testId={`check-confirmed-${a.id}`} title={t("dashboard.checkConfirmed")} /></div>
                            </td>
                            <td className="p-0 align-middle">
                              <div className="h-8">{isControl ? <div className="w-full h-full flex items-center justify-center text-stone-300">{NA}</div> : <CellPill ok={c.consent} testId={`check-consent-${a.id}`} title={t("dashboard.checkConsent")} />}</div>
                            </td>
                            <td className="p-0 align-middle">
                              <div className="h-8">{isControl ? <div className="w-full h-full flex items-center justify-center text-stone-300">{NA}</div> : <CellPill ok={c.anamnesi} testId={`check-anamnesi-${a.id}`} title={t("dashboard.checkAnamnesi")} />}</div>
                            </td>
                            <td className="p-0 align-middle">
                              <div className="h-8"><CellPill ok={c.intervention} testId={`check-intervention-${a.id}`} title={isControl ? t("dashboard.checkControl") : t("dashboard.checkIntervention")} /></div>
                            </td>
                            <td className="p-0 align-middle">
                              <div className="h-8"><CellPill ok={c.photos} testId={`check-photos-${a.id}`} title={t("dashboard.checkPhotos")} /></div>
                            </td>
                            <td className="p-0 align-middle">
                              <div className="h-8">{isControl ? <div className="w-full h-full flex items-center justify-center text-stone-300">{NA}</div> : <CellPill ok={c.invoice} testId={`check-invoice-${a.id}`} title={t("dashboard.checkInvoice")} />}</div>
                            </td>
                            <td className="p-0 align-middle text-center">
                              <button onClick={() => deleteAppt(a.id)} data-testid={`delete-appt-${a.id}`}
                                title={t("calendar.confirmDelete")}
                                className="text-stone-300 hover:text-red-500 p-1">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()
        )}
      </div>

      {/* SMALL: Monthly mini-calendar with appointment dots */}
      <div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="month-mini-calendar">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-xl font-light capitalize">{monthLabel}</h3>
            <div className="flex items-center gap-1">
              <button data-testid="cal-prev" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
                className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setMonthCursor(new Date())}
                className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900 px-2">
                {t("common.today")}
              </button>
              <button data-testid="cal-next" onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
                className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.15em] text-stone-400 mb-2">
            {weekdays.map((w, i) => <div key={i}>{w.slice(0, 3)}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.cells.map((d, i) => {
              if (!d) return <div key={i} className="aspect-square" />;
              const isToday = sameDay(d, today) && monthKey(monthCursor) === monthKey(today);
              const isSelected = sameDay(d, selectedDate);
              const count = grid.map[d.getDate()] || 0;
              return (
                <button key={i} type="button"
                  onClick={() => setSelectedDate(new Date(d))}
                  data-testid={`cal-day-${d.getDate()}`}
                  className={`aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-colors relative
                    ${isSelected ? "bg-[#D4A373] text-stone-900 ring-2 ring-[#B07D4F] ring-offset-1" :
                      isToday ? "bg-stone-900 text-white" : "hover:bg-[#FAF9F6] text-stone-700"}
                    ${count > 0 && !isSelected && !isToday ? "bg-[#FAF9F6]" : ""}`}>
                  <span>{d.getDate()}</span>
                  {count > 0 && (
                    <span className={`text-[9px] font-medium mt-0.5 ${
                      isSelected ? "text-stone-900" : isToday ? "text-[#D4A373]" : "text-[#B07D4F]"
                    }`}>
                      · {count} ·
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Booking card removed — now unified with the /dr/:slug link in PortalControls below */}
      </div>

      {/* Public link (custom slug) + Availability schedule */}
      <PortalControls />

      {/* New appointment modal */}
      {showNewAppt && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-appt-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowNewAppt(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-stone-100 flex items-center justify-between px-6 py-4">
              <h3 className="font-heading text-2xl font-light">{t("calendar.form.title")}</h3>
              <button onClick={() => setShowNewAppt(false)} data-testid="close-appt-modal"
                className="p-1 rounded-full hover:bg-stone-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submitAppt} className="p-6 space-y-4" data-testid="new-appointment-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.patient")} *</label>
                  <select value={apptForm.patient_id} onChange={(e) => setApptForm({ ...apptForm, patient_id: e.target.value })} required
                    data-testid="appt-patient"
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                    <option value="">{t("calendar.form.selectPatient")}</option>
                    {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}{p.phone ? ` · ${p.phone}` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.apptType")} *</label>
                  <select value={apptForm.type} onChange={(e) => setApptForm({ ...apptForm, type: e.target.value })} required
                    data-testid="appt-type"
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                    <option value="trattamento">{t("dashboard.typeTreatment")}</option>
                    <option value="controllo">{t("dashboard.typeControl")}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.treatment")} *</label>
                  <input value={apptForm.treatment} onChange={(e) => setApptForm({ ...apptForm, treatment: e.target.value })} required
                    data-testid="appt-treatment"
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.date")} *</label>
                  <input type="date" value={selectedIso} onChange={(e) => setSelectedDate(new Date(`${e.target.value}T00:00:00`))} required
                    data-testid="appt-date"
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.time")} *</label>
                  <input type="time" value={apptForm.time} onChange={(e) => setApptForm({ ...apptForm, time: e.target.value })} required
                    data-testid="appt-time"
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.duration")}</label>
                  <input type="number" value={apptForm.duration_min} onChange={(e) => setApptForm({ ...apptForm, duration_min: e.target.value })}
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.consentTemplate")}</label>
                  <select value={apptForm.consent_template_id} onChange={(e) => setApptForm({ ...apptForm, consent_template_id: e.target.value })}
                    className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                    <option value="">{t("calendar.form.noConsent")}</option>
                    {consentTemplates.map((tp) => <option key={tp.id} value={tp.id}>{tp.treatment} · {tp.original_filename}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
                <textarea value={apptForm.notes} onChange={(e) => setApptForm({ ...apptForm, notes: e.target.value })} rows={2}
                  spellCheck autoCorrect="on" autoCapitalize="sentences"
                  className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={savingAppt} data-testid="submit-appointment"
                  className="px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
                  {savingAppt ? t("common.saving") : t("common.save")}
                </button>
                <button type="button" onClick={() => setShowNewAppt(false)}
                  className="px-6 py-2.5 rounded-full border border-stone-200 hover:bg-stone-50">
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
